import React, { useState, useEffect, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { Button } from "@/components/ui/button";
import { Card, CardContent } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Badge } from "@/components/ui/badge";
import { Plus, Search, Package, Tag, List, Loader2, ChevronLeft, ChevronRight } from "lucide-react";
import { cn } from "@/lib/utils";
import { CreateProductModal } from "@/components/products/CreateProductModal";
import { CreateCategoryModal } from "@/components/products/CreateCategoryModal";
import { ProductDetail } from "@/components/products/ProductDetail";
import { ProductCard } from "@/components/products/ProductCard";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { useProducts } from "@/components/products/ProductsContext";
import { Product, ProductCategory } from "@/types/product";
import { useDebounce } from "@/hooks/useDebounce";
import { useAuth } from "@/hooks/useAuth";

export default function Products() {
  const {
    products,
    categories,
    isLoading,
    categoriesLoading,
    error,
    fetchProducts,
    createProduct,
    updateProduct,
    deleteProduct,
    createCategory,
    updateCategory,
    deleteCategory,
    slug,
    organizationName,
    totalItems,
    currentPage,
    totalPages,
    setCurrentPage,
  } = useProducts();

  const { can, company } = useAuth();
  const navigate = useNavigate();
  const [searchQuery, setSearchQuery] = useState("");
  const [selectedCategory, setSelectedCategory] = useState<string>("all");
  const [isProductModalOpen, setIsProductModalOpen] = useState(false);
  const [isCategoryModalOpen, setIsCategoryModalOpen] = useState(false);
  const [selectedProduct, setSelectedProduct] = useState<Product | null>(null);
  const [isDetailOpen, setIsDetailOpen] = useState(false);
  const [editingProduct, setEditingProduct] = useState<Product | null>(null);
  const [itemsPerPage, setItemsPerPage] = useState(12);

  const debouncedSearchQuery = useDebounce(searchQuery, 300);

  const getCurrencySymbol = () => {
    if ((company as any)?.currency) {
      return (company as any).currency;
    }
    try {
      const storedCompany = localStorage.getItem("company");
      if (storedCompany) {
        const parsed = JSON.parse(storedCompany);
        if (parsed.currency) return parsed.currency;
      }
    } catch (e) {}
    return "$";
  };
  const currencySymbol = getCurrencySymbol();

  const filteredProducts = useMemo(
    () =>
      products.filter((product) => {
        const matchesSearch =
          (product.name || "")
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase()) ||
          (product.sku || "")
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase()) ||
          (product.description || "")
            .toLowerCase()
            .includes(debouncedSearchQuery.toLowerCase());

        const matchesCategory =
          selectedCategory === "all" || product.categoryId === selectedCategory;

        return matchesSearch && matchesCategory;
      }),
    [products, debouncedSearchQuery, selectedCategory],
  );

  const totalFilteredPages = Math.ceil(filteredProducts.length / itemsPerPage);
  const paginatedProducts = filteredProducts.slice(
    (currentPage - 1) * itemsPerPage,
    currentPage * itemsPerPage,
  );

  useEffect(() => {
    setCurrentPage(1);
  }, [searchQuery, selectedCategory, setCurrentPage]);

  const handleSaveProduct = async (product: Product) => {
    try {
      if (editingProduct && editingProduct.id) {
        await updateProduct(editingProduct.id, product);
      } else {
        await createProduct(product);
      }
      setEditingProduct(null);
      setIsProductModalOpen(false);
      await fetchProducts({ page: currentPage, limit: itemsPerPage });
    } catch (error) {}
  };

  const handleDeleteProduct = async (id: string) => {
    try {
      await deleteProduct(id);
      await fetchProducts({ page: currentPage, limit: itemsPerPage });
    } catch (error) {}
  };

  const handleSaveCategory = async (category: ProductCategory) => {
    try {
      await createCategory(category);
    } catch (error) {}
  };

  const handleUpdateCategory = async (
    id: string,
    category: ProductCategory,
  ) => {
    try {
      await updateCategory(id, category);
    } catch (error) {}
  };

  const handleDeleteCategory = async (id: string) => {
    try {
      await deleteCategory(id);
    } catch (error) {}
  };

  const getCategoryName = (categoryId: string) => {
    const category = categories.find((c) => c.id === categoryId);
    return category?.name || "Sin categoría";
  };

  const getCategoryColor = (categoryId: string) => {
    const category = categories.find((c) => c.id === categoryId);
    return category?.color || "#6b7280";
  };

  const totalValue = products.reduce((sum, p) => sum + p.unitPrice, 0);
  const productsWithRules = products.filter(
    (p) => p.rules && p.rules.length > 0,
  ).length;

  return (
    <div className="w-full min-h-[calc(100vh-6rem)] flex flex-col p-8 bg-white rounded-[32px] shadow-[0_4px_40px_-12px_rgba(0,0,0,0.06)] border border-slate-200/60 space-y-6">
      <div className="flex flex-col sm:flex-row sm:items-center sm:justify-between gap-4">
        <div>
          <div className="flex items-center gap-2 mb-2">
            <div className="bg-indigo-100 p-2.5 rounded-2xl">
              <Package className="h-7 w-7 text-indigo-600" />
            </div>
            <h1 className="text-3xl font-bold text-slate-900 tracking-tight">
              Productos y Servicios
            </h1>
          </div>
          <p className="text-slate-500 text-sm md:text-base ml-1">
            Gestiona el catálogo de productos de {organizationName}
          </p>
        </div>
        <div className="flex flex-col sm:flex-row items-stretch sm:items-center gap-3">
          <div className="hidden sm:flex items-center gap-2">
            <Badge variant="outline" className="text-sm bg-white border-slate-200 text-slate-600 px-3 py-1 rounded-lg">
              {organizationName}
            </Badge>
          </div>
          <div className="flex flex-col sm:flex-row gap-2 w-full sm:w-auto">
            <Button
              variant="outline"
              onClick={() => setIsCategoryModalOpen(true)}
              disabled={categoriesLoading || !slug}
              className={cn("w-full sm:w-auto justify-center h-11 px-5 rounded-xl font-medium border-slate-200 text-slate-700 hover:bg-slate-50", !can("products.create") && "hidden")}
            >
              {categoriesLoading ? (
                <Loader2 className="h-4 w-4 mr-2 animate-spin text-slate-400" />
              ) : (
                <Tag className="h-4 w-4 mr-2 text-slate-400" />
              )}
              Categorías
            </Button>
            {can("products.create") && (
              <Button
                onClick={() => {
                  setEditingProduct(null);
                  setIsProductModalOpen(true);
                }}
                disabled={isLoading || categoriesLoading || !slug}
                className="w-full sm:w-auto justify-center h-11 px-5 rounded-xl font-medium bg-slate-900 hover:bg-black text-white shadow-sm"
              >
                <Plus className="h-4 w-4 mr-2" />
                Agregar Producto
              </Button>
            )}
          </div>
        </div>
      </div>

      {!slug && !isLoading && (
        <div className="p-4 bg-amber-50 border border-amber-200 rounded-md">
          <p className="text-amber-700">
            No se ha identificado la organización. Por favor, inicia sesión.
          </p>
        </div>
      )}

      {slug && (
        <>
          <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
            <Card className="rounded-[20px] border border-slate-100 shadow-sm bg-white">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Total Productos</p>
                    <p className="text-2xl font-bold text-slate-800">
                      {totalItems}
                    </p>
                  </div>
                  <div className="h-10 w-10 rounded-xl bg-blue-50 border border-blue-100 flex items-center justify-center">
                    <Package className="h-5 w-5 text-blue-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-[20px] border border-slate-100 shadow-sm bg-white">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">
                      Con Reglas de Precio
                    </p>
                    <p className="text-2xl font-bold text-slate-800">
                      {productsWithRules}
                    </p>
                  </div>
                  <div className="h-10 w-10 rounded-xl bg-emerald-50 border border-emerald-100 flex items-center justify-center">
                    <List className="h-5 w-5 text-emerald-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-[20px] border border-slate-100 shadow-sm bg-white">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Categorías</p>
                    <p className="text-2xl font-bold text-slate-800">
                      {categories.filter((c) => c.id !== "").length}
                    </p>
                  </div>
                  <div className="h-10 w-10 rounded-xl bg-purple-50 border border-purple-100 flex items-center justify-center">
                    <Tag className="h-5 w-5 text-purple-600" />
                  </div>
                </div>
              </CardContent>
            </Card>

            <Card className="rounded-[20px] border border-slate-100 shadow-sm bg-white">
              <CardContent className="p-5">
                <div className="flex items-center justify-between">
                  <div>
                    <p className="text-[11px] font-bold text-slate-400 uppercase tracking-wider mb-1">Valor Catálogo</p>
                    <p className="text-2xl font-bold text-slate-800">
                      {currencySymbol}{(totalValue / 1000).toFixed(1)}K
                    </p>
                  </div>
                  <div className="h-10 w-10 rounded-xl bg-orange-50 border border-orange-100 flex items-center justify-center text-orange-600 font-bold text-lg">
                    {currencySymbol}
                  </div>
                </div>
              </CardContent>
            </Card>
          </div>

          <div className="flex flex-col sm:flex-row gap-4">
            <div className="relative flex-1">
              <Search className="absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-slate-400" />
              <Input
                placeholder="Buscar productos..."
                value={searchQuery}
                onChange={(e) => setSearchQuery(e.target.value)}
                className="pl-11 h-11 bg-white border-slate-200 rounded-xl shadow-sm focus-visible:ring-indigo-500"
                disabled={isLoading || categoriesLoading}
              />
            </div>
            <Select
              value={selectedCategory}
              onValueChange={setSelectedCategory}
              disabled={isLoading || categoriesLoading}
            >
              <SelectTrigger className="w-full sm:w-64 h-11 bg-white border-slate-200 rounded-xl shadow-sm focus:ring-indigo-500">
                <SelectValue placeholder="Todas las categorías" />
              </SelectTrigger>
              <SelectContent className="rounded-xl border-slate-100 shadow-xl">
                <SelectItem value="all" className="rounded-lg">Todas las categorías</SelectItem>
                {categories
                  .filter(
                    (cat) => cat.id && cat.id.trim() !== "" && cat.id !== "",
                  )
                  .map((cat) => (
                    <SelectItem key={cat.id} value={cat.id} className="rounded-lg">
                      <div className="flex items-center gap-2">
                        <div
                          className="h-2.5 w-2.5 rounded-full"
                          style={{ backgroundColor: cat.color || "#6b7280" }}
                        />
                        <span className="font-medium text-slate-700">{cat.name}</span>
                      </div>
                    </SelectItem>
                  ))}
              </SelectContent>
            </Select>
          </div>

          {(isLoading || categoriesLoading) && (
            <div className="flex justify-center items-center py-12">
              <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-blue-600"></div>
              <span className="ml-3">Cargando...</span>
            </div>
          )}

          {!isLoading && !categoriesLoading && paginatedProducts.length > 0 && (
            <>
              <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 xl:grid-cols-4 gap-4">
                {paginatedProducts.map((product) => (
                  <ProductCard
                    key={product.id}
                    product={product}
                    onEdit={(p) => {
                      setEditingProduct(p);
                      setIsProductModalOpen(true);
                    }}
                    onDelete={handleDeleteProduct}
                    onClick={(p) => {
                      setSelectedProduct(p);
                      setIsDetailOpen(true);
                    }}
                    getCategoryName={getCategoryName}
                    getCategoryColor={getCategoryColor}
                  />
                ))}
              </div>

              {filteredProducts.length > 0 && (
                <div className="flex flex-col items-center gap-6 mt-8">
                  <div className="flex items-center justify-center gap-2">
                    <Button 
                      variant="outline" 
                      size="icon" 
                      className="rounded-full h-10 w-10 border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-indigo-600 transition-colors"
                      onClick={() => setCurrentPage(Math.max(1, currentPage - 1))}
                      disabled={currentPage === 1}
                    >
                      <ChevronLeft className="h-5 w-5" />
                    </Button>
                    <div className="flex items-center gap-1">
                      {Array.from({ length: totalFilteredPages }).map((_, i) => (
                        <button
                          key={i}
                          onClick={() => setCurrentPage(i + 1)}
                          className={`h-10 w-10 rounded-full text-sm font-medium transition-all ${
                            currentPage === i + 1 
                              ? "bg-slate-900 text-white shadow-md shadow-slate-900/30" 
                              : "text-slate-600 hover:bg-slate-100 hover:text-indigo-600"
                          }`}
                        >
                          {i + 1}
                        </button>
                      ))}
                    </div>
                    <Button 
                      variant="outline" 
                      size="icon" 
                      className="rounded-full h-10 w-10 border-slate-200 text-slate-500 hover:bg-slate-50 hover:text-indigo-600 transition-colors"
                      onClick={() => setCurrentPage(Math.min(totalFilteredPages, currentPage + 1))}
                      disabled={currentPage === totalFilteredPages}
                    >
                      <ChevronRight className="h-5 w-5" />
                    </Button>
                  </div>
                  <div className="text-sm font-medium text-slate-500 bg-slate-50 px-4 py-2 rounded-full border border-slate-100">
                    Mostrando <span className="text-slate-900">{paginatedProducts.length}</span> de <span className="text-slate-900">{filteredProducts.length}</span> productos
                  </div>
                </div>
              )}
            </>
          )}

          {!isLoading &&
            !categoriesLoading &&
            filteredProducts.length === 0 && (
              <div className="text-center py-12">
                <Package className="h-16 w-16 text-slate-300 mx-auto mb-4" />
                <h3 className="text-lg font-medium text-slate-900 mb-2">
                  No hay productos
                </h3>
                <p className="text-slate-600 mb-4">
                  {searchQuery || selectedCategory !== "all"
                    ? "No se encontraron productos con ese criterio"
                    : "Comienza creando tu primer producto"}
                </p>
                {!searchQuery && selectedCategory === "all" && (
                  <Button
                    onClick={() => setIsProductModalOpen(true)}
                    disabled={categoriesLoading || !slug}
                    className={!can("products.create") ? "hidden" : ""}
                  >
                    <Plus className="h-4 w-4 mr-2" />
                    Agregar Producto
                  </Button>
                )}
              </div>
            )}
        </>
      )}

      <CreateProductModal
        isOpen={isProductModalOpen}
        onClose={() => {
          setIsProductModalOpen(false);
          setEditingProduct(null);
        }}
        onSave={handleSaveProduct}
        categories={categories.filter((cat) => cat.id !== "")}
        editingProduct={editingProduct}
      />

      <CreateCategoryModal
        isOpen={isCategoryModalOpen}
        onClose={() => {
          setIsCategoryModalOpen(false);
        }}
        onSave={handleSaveCategory}
        onUpdate={(category) => {
          handleUpdateCategory(category.id, category);
        }}
        onDelete={(categoryId) => {
          handleDeleteCategory(categoryId);
        }}
        categories={categories}
        slug={slug}
      />

      <ProductDetail
        product={selectedProduct}
        isOpen={isDetailOpen}
        onClose={() => {
          setIsDetailOpen(false);
          setSelectedProduct(null);
        }}
        categories={categories}
      />
    </div>
  );
}
